import { DOCUMENT, NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { LinkComponent, SvgComponent } from '@app/base-components';
import { ButtonQaId, HabaQaId } from '@app/helpers';
import { AuthService, I18nModule, WindowRef } from 'haba-core';
import { filter, startWith, Subject, take, takeUntil, tap } from 'rxjs';
import { NavigationComponent } from 'src/app/header/base/navigation.component';
import { PrefixboxService } from 'haba-emporix';
import { ImageComponent } from 'src/app/product/product-card-base/image/image.component';
import { Entry } from 'contentful';
import {
  NavigationNode,
  NavigationQuickCall,
} from 'haba-contentful-components/lib/interfaces/navigation';
import { ProductDetailsService } from 'src/app/services/product-details/product-details.service';

@Component({
  selector: 'app-shop-nav-mobile',
  templateUrl: './shop-nav-mobile.component.html',
  styleUrls: ['./shop-nav-mobile.component.scss'],
  imports: [
    NgFor,
    NgIf,
    LinkComponent,
    NgClass,
    I18nModule,
    RouterLink,
    SvgComponent,
    ImageComponent,
  ],
})
export class ShopNavMobileComponent
  extends NavigationComponent
  implements OnChanges, AfterViewInit, OnInit, OnDestroy
{
  @ViewChild('scrollWrapper') scrollWrapper!: ElementRef<HTMLUListElement>;

  @Input() navigationNodes: Entry<NavigationNode>[];
  @Input() quickLinks: Entry<NavigationQuickCall>[];
  @Input() inScroll: boolean;

  baseId: HabaQaId;
  headerNavigationLink: ButtonQaId;
  mainCategoryActiveIndex: number = -1;
  categoryTree;

  atStart = true;
  atEnd = false;

  onClient = false;

  private onDestroy$ = new Subject();

  constructor(
    @Inject(DOCUMENT) protected documentRef: Document,
    @Inject(PLATFORM_ID) platformId: string,
    authService: AuthService,
    protected winRef: WindowRef,
    protected router: Router,
    elemRef: ElementRef,
    private prefixboxService: PrefixboxService,
    private productDetailsService: ProductDetailsService
  ) {
    super(documentRef, platformId, authService, winRef, elemRef, router);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initIds();

    if (this.winRef.isBrowser()) {
      this.onClient = true;
    }
  }

  ngOnChanges() {
    if (this.navigationNodes) {
      this.prefixboxService
        .getCategoryTree()
        .pipe(take(1))
        .subscribe((categoryTree) => {
          const shopNode = this.navigationNodes.find(
            (node) => node.fields.name.toLowerCase() === 'shop'
          );
          if (!shopNode) return;

          const shopNodeUrls = shopNode.fields.childNavigationNodes.map((node) => node.fields.url);
          this.categoryTree = categoryTree.filter((category) =>
            shopNodeUrls.some((nodeUrl) => nodeUrl.includes(category.code))
          );

          this.presetSelectedMainCategory();
        });
    }
  }

  ngAfterViewInit() {
    this.checkScroll();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }

  onScroll() {
    this.checkScroll();
  }

  get isShopRelatedPage() {
    const urlSegments = this.router.url.split('/');
    return (
      urlSegments[1] === 'shop' ||
      urlSegments[1] === 'c' ||
      urlSegments[1] === 'p' ||
      urlSegments[1] === 'cart'
    );
  }

  get currentPath() {
    const [noQueryParamsMainCategory] = this.router.url.split('?');
    const [, pageType, mainCategory] = noQueryParamsMainCategory.split('/');
    if (!mainCategory) return;
    return `/${pageType}/${mainCategory}`;
  }

  private presetSelectedMainCategory() {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => this.productDetailsService.productCategoryPath$.next([])),
        filter((event) => event instanceof NavigationEnd),
        startWith(null)
      )
      .subscribe(() => {
        const [, pageType] = this.router.url.split('/');
        const onProductPage = pageType === 'p';

        this.mainCategoryActiveIndex = -1;

        if (onProductPage) {
          this.productDetailsService.productCategoryPath$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((productCategoryPath) => {
              this.mainCategoryActiveIndex = this.categoryTree.findIndex(
                (category) => category.url === productCategoryPath[0]
              );
            });
        }
      });
  }

  private checkScroll() {
    if (!this.scrollWrapper) return;

    const el = this.scrollWrapper.nativeElement;

    this.atStart = el.scrollLeft <= 0;
    this.atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth;
  }

  private initIds() {
    this.baseId = HabaQaId.forPage(HabaQaId.page.header);
    this.headerNavigationLink = this.baseId.getButtonId(
      HabaQaId.elements.button.headerNavigationLink
    );
  }
}
